import { z } from "zod";
import {
  exchangeVariantSchema,
  ReturnProductSchema,
} from "../../../draft-return/draft-return-items";
import { AiReturnReasonSchema } from "../return-reasons";

export const SuggestedVariantOptionsSchema = z.object({
  shouldSuggest: z
    .boolean()
    .describe(
      "Whether the customer should be suggested a variant option at all. If false, the other fields in the schema can be empty.",
    ),
  variantSuggestion: z.string().describe(`
    A single question that can be phrased in one of two ways depending on what the suggestions are:

    1. If suggested options are for a different variant, the question should be phrased as:
    'Would you like to exchange this for a different <variant-category-name>?' Where <variant-category-name> is the category name of the variants that are most likely reason for the return. For example: 'size' or 'color'.

    2. If the suggestion is to exchange for the same variant as the original returned item, the question should be phrased as:
    'Would you like to exchange this for the another <product-name>?' Where <product-name> is the product name of the original returned item.
    If the name of the product is very long, consider generalizing this sentence to 'Would you like to exchange this for the same item?'

    If the resulting sentence doesn't make sense, try to rephrase it to make it more natural.`),
  variantSuggestedResolution: z.string().describe(`
    A short apology that should be similarly phrased to 'We're sorry to hear this product <reason-for-return>.' Where <reason-for-return> is the most likely reason for the return. For example: 'didn't fit well' or 'wasn't the color you wanted'.

    This should be followed by a single statement that should be phrased in one of two ways depending on what the suggestions are:

    1. If suggested options are for a different variant, the statement should be phrased as:
    'We can exchange it for a <variant-option>.' Where <variant-option> is the type of option for the variant that the customer should consider exchanging for. For example: 'bigger size' or 'different color'.

    2. If the suggestion is to exchange for the same variant as the original returned item, the statement should be phrased as:
    'We can replace it, or exchange it for something similar!'

    If the resulting sentences don't make sense or are long, try to rephrase them to make it more natural and concise.`),
  suggestedOptions: z.array(
    z.object({ name: z.string(), values: z.array(z.string()) }),
  ).describe(`
      An array of available variant options that the customer would likely want to exchange for.
      For example: [{ name: 'size', values: ['Large', 'Extra Large'] }, { name: 'color', values: ['Red', 'Blue'] }]`),
});

export const SuggestedVariantOptionsInputSchema = z.object({
  draftReturnId: z.string(),
  product: ReturnProductSchema,
  availableVariants: z.array(exchangeVariantSchema),
  reasons: z.array(AiReturnReasonSchema),
});

export type SuggestedVariantOptions = z.infer<
  typeof SuggestedVariantOptionsSchema
>;

export type SuggestedVariantOptionsInput = z.infer<
  typeof SuggestedVariantOptionsInputSchema
>;
